import { useLocalization } from "@fluent/react";
import { Button, ContentCard } from "@narmi/design_system";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { CopyableText } from "@lending/components/CopyableText/CopyableText";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useNavigateWithSearch } from "@lending/hooks/useNavigateWithSearch";
import { useProductType } from "@lending/hooks/useProductType";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import styles from "./VerifyEmail.module.css";
import {
  locateApplicant,
  verifyApplicantCode,
} from "../Activate/hooks/useMembershipShareApplicantVerification";
import { getMembershipShareEndpoint } from "../hooks/getMembershipShareEndpoint";
import { useMembershipShareApplication } from "../hooks/useMembershipShareApplication";

const CODE_LENGTH = 6;

function VerifyEmail() {
  const { l10n } = useLocalization();
  const navigate = useNavigateWithSearch();
  const queryClient = useQueryClient();
  const applicationType = useProductType();
  const { goBack } = useFlowNavigation();
  const { showErrorToast, showToast } = useToastsContext();
  const { application, isLoading, isRefetching, isError } = useMembershipShareApplication();
  const membershipShareAccessEndpoint = getMembershipShareEndpoint(
    applicationType,
    "membership_share_access"
  );

  const email = application?.primary_applicant?.email ?? "";
  const [values, setValues] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [demoCode, setDemoCode] = useState<string | undefined>();
  const [isResendingCode, setIsResendingCode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const requestedInitialCodeForEmail = useRef<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const code = useMemo(() => values.join(""), [values]);
  const isComplete = values.every(Boolean);
  const nextPath = application?.state === "membership_selection" ? "select" : "activate";

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (
      isLoading ||
      isRefetching ||
      isError ||
      !application ||
      !email ||
      requestedInitialCodeForEmail.current === email
    ) {
      return;
    }

    requestedInitialCodeForEmail.current = email;

    const requestInitialCode = async () => {
      try {
        const { response, data } = await locateApplicant(email);
        if (!response.ok) {
          throw new Error(`Failed to send verification code: ${response.status}`);
        }

        setDemoCode(data?.code ?? undefined);
      } catch (requestError) {
        Sentry.captureException(requestError, { extra: { email } });
        showErrorToast(
          l10n.getString(
            "membership-share-verify-email-resend-error",
            null,
            "There was an error sending your verification code. Please try again."
          )
        );
      }
    };

    void requestInitialCode();
  }, [application, email, isError, isLoading, isRefetching, l10n, showErrorToast]);

  if (isLoading || isRefetching) {
    return (
      <PageLayout isLoading className={styles.pageLayout}>
        {null}
      </PageLayout>
    );
  }

  if (isError || !application || !email) {
    return <ErrorScreen />;
  }

  const handleResendCode = async () => {
    setIsResendingCode(true);

    try {
      const { response, data: verificationData } = await locateApplicant(email);
      if (!response.ok) {
        throw new Error(`Failed to resend code: ${response.status}`);
      }

      setDemoCode(verificationData?.code ?? undefined);
      showToast({
        type: "success",
        text: l10n.getString("membership-share-verify-email-code-resent", null, "Code resent"),
      });
    } catch (resendError) {
      Sentry.captureException(resendError, { extra: { email } });
      showErrorToast(
        l10n.getString(
          "membership-share-verify-email-resend-error",
          null,
          "There was an error sending your verification code. Please try again."
        )
      );
    } finally {
      setIsResendingCode(false);
    }
  };

  const handleContinue = async () => {
    setIsSubmitting(true);

    try {
      const response = await verifyApplicantCode(email, code);
      if (!response.ok) {
        throw new Error(`Invalid verification code: ${response.status}`);
      }

      await queryClient.invalidateQueries({
        queryKey: ["get", membershipShareAccessEndpoint],
      });

      if (nextPath === "select") {
        void navigate("../select");
        return;
      }

      void navigate("../activate");
    } catch (verifyError) {
      Sentry.captureException(verifyError, { extra: { email } });
      showErrorToast(
        l10n.getString(
          "membership-share-verify-email-invalid-code-error",
          null,
          "The verification code was invalid or expired. Please try again."
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (index: number, nextValue: string) => {
    if (nextValue && !/^\d$/.test(nextValue)) {
      return;
    }

    const nextValues = [...values];
    nextValues[index] = nextValue;
    setValues(nextValues);

    if (nextValue && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !values[index] && index > 0) {
      event.preventDefault();
      const nextValues = [...values];
      nextValues[index - 1] = "";
      setValues(nextValues);
      inputRefs.current[index - 1]?.focus();
      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedData = event.clipboardData
      .getData("text/plain")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);

    if (!pastedData) {
      return;
    }

    const nextValues = pastedData
      .split("")
      .concat(Array(CODE_LENGTH).fill(""))
      .slice(0, CODE_LENGTH);
    setValues(nextValues);
    inputRefs.current[Math.min(pastedData.length, CODE_LENGTH) - 1]?.focus();
  };

  return (
    <PageLayout className={styles.pageLayout}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h1 className="margin--bottom--xs fontFamily--heading">
              {l10n.getString("membership-share-verify-email-heading", null, "Verify your email")}
            </h1>
          </div>

          <div className={styles.card}>
            <ContentCard radiusSize="l" kind="bordered" paddingSize="none">
              <div className={styles.cardContent}>
                <div className={styles.emailText}>
                  {l10n.getString(
                    "membership-share-verify-email-subheading",
                    null,
                    "Enter the code sent to"
                  )}{" "}
                  <span className={styles.emailAddress}>{email}</span>
                </div>

                {demoCode && (
                  <div className={styles.demoCodeText}>
                    <span>
                      {l10n.getString(
                        "membership-share-verify-email-demo-code-prefix",
                        null,
                        "For this demo, please enter"
                      )}{" "}
                    </span>
                    <CopyableText text={demoCode} className={styles.demoCodeButton} />
                    <span>
                      {" "}
                      {l10n.getString(
                        "membership-share-verify-email-demo-code-suffix",
                        null,
                        "below"
                      )}
                    </span>
                  </div>
                )}

                <div className={styles.otpContainer}>
                  <div className={styles.otpGroup}>
                    {values.map((value, index) => (
                      <Fragment key={`membership-share-verification-${index}`}>
                        <input
                          ref={(element) => {
                            inputRefs.current[index] = element;
                          }}
                          aria-label={l10n.getString(
                            "ui-otp-input-aria-label",
                            { position: index + 1, total: CODE_LENGTH },
                            `Digit ${index + 1} of ${CODE_LENGTH}`
                          )}
                          className={styles.otpInput}
                          inputMode="numeric"
                          maxLength={1}
                          type="text"
                          value={value}
                          onChange={(event) => handleChange(index, event.target.value)}
                          onKeyDown={(event) => handleKeyDown(index, event)}
                          onPaste={handlePaste}
                        />
                        {index === 2 && <div className={styles.separator} aria-hidden="true" />}
                      </Fragment>
                    ))}
                  </div>
                </div>

                <div className={styles.resendButtonRow}>
                  <Button
                    kind="plain"
                    label={l10n.getString(
                      "membership-share-verify-email-resend-code",
                      null,
                      "Resend code"
                    )}
                    size="s"
                    onClick={() => void handleResendCode()}
                    isLoading={isResendingCode}
                  />
                </div>
              </div>
            </ContentCard>
          </div>

          <div className={styles.buttonRow}>
            <BackButton onClick={() => goBack()} />
            <Button
              label={l10n.getString("button-label-continue", null, "Continue")}
              onClick={() => void handleContinue()}
              disabled={!isComplete}
              isLoading={isSubmitting}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

export { VerifyEmail };
